import React, { useState } from 'react';
import { 
  ChevronRight, 
  Calendar, 
  FileText, 
  ClipboardList, 
  GraduationCap,
  BookOpen,
  DollarSign,
  HelpCircle,
  CheckCircle,
  Clock,
  Users,
  Award,
  Download,
  Mail,
  Phone,
  MapPin,
  ArrowRight
} from 'lucide-react';

const AdmissionsSection = () => {
  const [activeTab, setActiveTab] = useState('process');

  const programs = [
    {
      name: "Bachelor of Computer Applications (BCA)",
      duration: "3 Years",
      eligibility: "10+2 with Mathematics/Computer Science (50% marks)",
      seats: 120,
      icon: GraduationCap
    },
    {
      name: "Master of Computer Applications (MCA)",
      duration: "2 Years",
      eligibility: "BCA/B.Sc. with Mathematics (50% marks)",
      seats: 60,
      icon: GraduationCap
    },
    {
      name: "Bachelor of Business Administration (BBA)",
      duration: "3 Years",
      eligibility: "10+2 from any stream (50% marks)",
      seats: 100,
      icon: BookOpen
    },
    {
      name: "Master of Business Administration (MBA)",
      duration: "2 Years",
      eligibility: "Graduation with 50% marks + CAT/MAT/State Exam",
      seats: 80,
      icon: Award
    }
  ];

  const steps = [
    {
      title: "Application Form",
      description: "Fill the online application form with personal and academic details",
      icon: FileText
    },
    {
      title: "Document Upload",
      description: "Upload required documents (marksheets, ID proof, photographs)",
      icon: ClipboardList
    },
    {
      title: "Application Review",
      description: "Our team reviews your application and documents",
      icon: CheckCircle
    },
    {
      title: "Entrance Exam/Interview",
      description: "Appear for entrance exam or personal interview as per program",
      icon: Users
    },
    {
      title: "Merit List",
      description: "Selected candidates appear in the merit list",
      icon: Award
    },
    {
      title: "Fee Payment",
      description: "Confirm your admission by paying the admission fee",
      icon: DollarSign
    }
  ];

  const documents = [
    "10th Marksheet & Certificate",
    "12th Marksheet & Certificate",
    "Graduation Marksheets (for PG courses)",
    "Transfer Certificate (TC)",
    "Character Certificate",
    "Caste Certificate (if applicable)",
    "Income Certificate (if applicable)",
    "Passport Size Photographs (4)",
    "ID Proof (Aadhar Card)",
    "Migration Certificate"
  ];

  const importantDates = [
    { event: "Application Start Date", date: "March 1, 2024" },
    { event: "Application Deadline", date: "May 31, 2024" },
    { event: "Entrance Exam Date", date: "June 15, 2024" },
    { event: "Merit List Announcement", date: "June 25, 2024" },
    { event: "Counseling & Admission", date: "July 1-10, 2024" },
    { event: "Classes Commence", date: "July 20, 2024" }
  ];

  const faqs = [
    {
      question: "What is the minimum percentage required for admission?",
      answer: "For UG courses, minimum 50% marks in 10+2. For PG courses, minimum 50% marks in graduation. Relaxation as per government norms for reserved categories."
    },
    {
      question: "Is there any entrance exam for admission?",
      answer: "Yes, for some professional courses like MBA and MCA, we conduct our own entrance test. For other courses, admission is based on merit."
    },
    {
      question: "Can I apply for multiple courses?",
      answer: "Yes, you can apply for up to 3 courses. You need to fill separate application forms for each course."
    },
    {
      question: "What is the application fee?",
      answer: "The application fee is ₹500 for general category and ₹300 for reserved categories. It can be paid online through credit/debit card or net banking."
    },
    {
      question: "Is there a hostel facility available?",
      answer: "Yes, we have separate hostels for boys and girls with all modern amenities. Hostel allocation is on a first-come-first-serve basis."
    }
  ];

  return (
    <div className="bg-white">
      <div className="relative bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-blue-200 mb-4">
            <span className="hover:text-orange-400 cursor-pointer">Home</span>
            <ChevronRight size={16} />
            <span className="text-orange-400 font-semibold">Admissions</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Admissions </h1>
          <p className="text-xl text-blue-100 max-w-2xl">Begin your journey towards a brighter future with RDS College</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center transform hover:scale-105 transition-all duration-300">
            <div className="inline-flex p-3 bg-orange-100 rounded-full mb-3">
              <Calendar className="w-6 h-6 text-orange-500" />
            </div>
            <div className="text-sm font-semibold text-gray-600">Applications Open</div>
            <div className="text-lg font-bold text-blue-900">March 1, 2024</div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center transform hover:scale-105 transition-all duration-300">
            <div className="inline-flex p-3 bg-orange-100 rounded-full mb-3">
              <Clock className="w-6 h-6 text-orange-500" />
            </div>
            <div className="text-sm font-semibold text-gray-600">Last Date</div>
            <div className="text-lg font-bold text-blue-900">May 31, 2024</div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center transform hover:scale-105 transition-all duration-300">
            <div className="inline-flex p-3 bg-orange-100 rounded-full mb-3">
              <Users className="w-6 h-6 text-orange-500" />
            </div>
            <div className="text-sm font-semibold text-gray-600">Total Seats</div>
            <div className="text-lg font-bold text-blue-900">500+</div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center transform hover:scale-105 transition-all duration-300">
            <div className="inline-flex p-3 bg-orange-100 rounded-full mb-3">
              <GraduationCap className="w-6 h-6 text-orange-500" />
            </div>
            <div className="text-sm font-semibold text-gray-600">Programs</div>
            <div className="text-lg font-bold text-blue-900">15+</div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          <button
            onClick={() => setActiveTab('process')}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              activeTab === 'process'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Admission Process
          </button>
          <button
            onClick={() => setActiveTab('programs')}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              activeTab === 'programs'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Programs
          </button>
          <button
            onClick={() => setActiveTab('dates')}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              activeTab === 'dates'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Important Dates
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              activeTab === 'documents'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Documents
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              activeTab === 'faq'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            FAQs
          </button>
        </div>
        <div className="mt-8">
          {activeTab === 'process' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Admission Process</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {steps.map((step, index) => (
                  <div key={index} className="relative bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 group">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <button className="inline-flex items-center space-x-2 bg-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-orange-600 transition-colors duration-300">
                  <span>Apply Now</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}
          {activeTab === 'programs' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Our Programs</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {programs.map((program, index) => (
                  <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl flex items-center justify-center">
                        <program.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold">
                        {program.seats} Seats
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-blue-900 mb-2">{program.name}</h3>
                    <div className="space-y-2 mb-4">
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Duration:</span> {program.duration}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Eligibility:</span> {program.eligibility}
                      </p>
                    </div>
                    <button className="text-orange-500 font-semibold flex items-center space-x-1 hover:space-x-2 transition-all duration-300">
                      <span>Apply Now</span>
                      <ArrowRight size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'dates' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Important Dates</h2>
              <div className="max-w-3xl mx-auto">
                {importantDates.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-4 border-b border-gray-200 last:border-0">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-orange-500" />
                      <span className="text-gray-700">{item.event}</span>
                    </div>
                    <span className="font-semibold text-blue-900">{item.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'documents' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Documents Required</h2>
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {documents.map((doc, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{doc}</span>
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <button className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300">
                  <Download size={18} />
                  <span>Download Checklist</span>
                </button>
              </div>
            </div>
          )}
          {activeTab === 'faq' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Frequently Asked Questions</h2>
              <div className="max-w-3xl mx-auto space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-start space-x-3">
                      <HelpCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-blue-900 mb-2">{faq.question}</h3>
                        <p className="text-gray-600 text-sm">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Need Help? Contact Us</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="font-semibold text-blue-900 mb-2">Call Us</h3>
            <p className="text-gray-600">+91 7275370808</p>
            <p className="text-gray-600">+91 9628362187</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="font-semibold text-blue-900 mb-2">Email Us</h3>
            <p className="text-gray-600">admissions@rdscollege.edu</p>
            <p className="text-gray-600">info@rdscollege.edu</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="font-semibold text-blue-900 mb-2">Visit Us</h3>
            <p className="text-gray-600">Admission Office, RDS College</p>
            <p className="text-gray-600">City, State - 123456</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionsSection;